import { useState } from 'react';
import { Button } from '../../../atoms/Button/Button';
import { TableHeader } from '../../../molecules/TableHeader/TableHeader';
import { Table } from '../../../organisms/Table/Table';
import { spanClass, type WidgetBlockProps, type WidgetStatus } from '../shared/cardShell';
import { hashtagColumns, hashtagTableRows, type HashtagEntry } from './hashtagTable';
import { TableWidgetActions, tableDraggingClass } from './tableChrome';
import { TopHashtagsModal } from './TopHashtagsModal';

/**
 * How many tags the widget previews — the eight the frame draws. The whole
 * ranking lives in the modal `View all` opens.
 */
const HASHTAG_PREVIEW = 8;

export type TopHashtagsView = {
  /** Every tag in scope, ranked by mentions — the widget previews the first eight. */
  entries: HashtagEntry[];
  /** The modal's header badge, e.g. `10 hashtags`. */
  countLabel: string;
};

export type TopHashtagsTableProps = WidgetBlockProps & {
  view?: TopHashtagsView;
};

/**
 * What the table's single empty band says. A `Table`'s `emptyLabel` is a plain
 * string, so the error state carries no `Try again` button here (the card
 * shell's does) — the message only.
 */
function emptyLabelFor(status: WidgetStatus): string {
  if (status === 'loading') return 'Loading…';
  if (status === 'error') return 'Couldn’t load this widget.';
  if (status === 'notConnected') return 'Live data for this widget is not connected yet.';
  return 'No hashtag posts in this period';
}

/**
 * Figma `947:19576` — the tags people attach to posts about us.
 *
 * **Recut 2026-09-23** (table 951:26706). The pagination went, the `Rank`
 * column with it, and a `View all` button arrived in the header: the widget is
 * the top eight, and the whole ranking lives in the modal that button opens.
 * The ranking itself is shared with that modal — see `hashtagTable`.
 *
 * The modal is mounted here rather than by the page, because this table can
 * sit on a custom report, where there is no view to ask for a dialog, and a
 * `View all` that did nothing there would be a control that lies.
 */
export function TopHashtagsTable({ status, view, span = 6, chrome }: TopHashtagsTableProps) {
  const [allOpen, setAllOpen] = useState(false);

  const entries = status === 'ready' && view ? view.entries : [];

  return (
    <>
      <Table
        className={[spanClass(span), tableDraggingClass(chrome)].filter(Boolean).join(' ')}
        label="Top hashtags used, ranked by mentions"
        header={
          <TableHeader
            flash={chrome?.flash}
            title="Top hashtags used"
            helpText="Hashtags in monitored public posts, ranked by the number of mentions containing each tag. Sentiment shows the most common tone around each hashtag."
            actions={
              <>
                {/*
                 * Figma `Hierarchy=Ghost Color`, no icon. Opens the whole
                 * ranking (2211:92097) — the eight below it are a preview, and
                 * without this there is now no way to reach the ninth.
                 */}
                <Button hierarchy="ghostColor" onClick={() => setAllOpen(true)}>
                  View all
                </Button>
                {/* On a report, the drag handle and the remove button — see `TableWidgetActions`. */}
                <TableWidgetActions chrome={chrome} title="Top hashtags used" />
              </>
            }
          />
        }
        /* The frame's own widths — 168 / 81 / 168 / 168 of its 586. */
        columns={hashtagColumns(['28.7%', '13.8%', '28.7%', '28.7%'])}
        rows={hashtagTableRows(entries.slice(0, HASHTAG_PREVIEW))}
        emptyLabel={emptyLabelFor(status)}
      />

      <TopHashtagsModal
        open={allOpen}
        onClose={() => setAllOpen(false)}
        entries={entries}
        countLabel={view?.countLabel}
      />
    </>
  );
}
